<!--
 *@Author: 作者名 lx
 *@Date: 2026-01-15
 *@Description: 系列详情页 - 展示系列信息和文章列表
-->
<script setup lang="ts">
const config = useRuntimeConfig()
const route = useRoute()

// 类型定义
interface Series {
  SeriesId: number
  Name: string
  Slug: string
  Description: string | null
  CoverImage: string | null
  TypeId: number | null
  IsPublic: number
  SortOrder: number
  CreatedAt: string
  UpdatedAt: string
}

interface Article {
  ArticleId: number
  Title: string
  Slug: string
  Summary: string | null
  CoverImage: string | null
  CoverBlurHash: string | null
  TypeId: number
  TypeName?: string
  SeriesId: number | null
  SeriesOrder: number
  IsPublic: number
  CreatedAt: string
  UpdatedAt: string
}

interface ApiResponse {
  series: Series
  articles: Article[]
}

const slug = computed(() => route.params.slug as string)

// 获取系列详情
const { data, status, error } = await useAsyncData(
  `series-${slug.value}`,
  async () => {
    return await $fetch<ApiResponse>(`${config.public.apiBase}/series/${slug.value}`)
  },
  { server: true }
)

const pending = computed(() => status.value === 'pending')
const series = computed(() => data.value?.series)
const articles = computed(() => data.value?.articles || [])

// 工具函数
const getImageUrl = (key: string | null) => {
  if (!key) return ''
  if (key.startsWith('http')) return key
  const imageBase = 'https://image.lixiang.page'
  return `${imageBase}/${key}`
}

const formatDate = (dateStr: string) => {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

// SEO
useHead({
  title: () => series.value ? `${series.value.Name} - 系列` : '系列',
  meta: [
    {
      name: 'description',
      content: () => series.value?.Description || `${series.value?.Name} 系列文章`
    }
  ]
})
</script>

<template>
  <div class="series-page">
    <!-- 加载状态 -->
    <div v-if="pending" class="loading">
      <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50">
        <path fill="#5B6FB5"
          d="M43.935,25.145c0-10.318-8.364-18.683-18.683-18.683c-10.318,0-18.683,8.365-18.683,18.683h4.068c0-8.071,6.543-14.615,14.615-14.615c8.072,0,14.615,6.543,14.615,14.615H43.935z">
          <animateTransform attributeType="xml" attributeName="transform" type="rotate" from="0 25 25" to="360 25 25"
            dur=".8s" repeatCount="indefinite" />
        </path>
      </svg>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error">
      <h2>系列不存在</h2>
      <p>您访问的系列可能已被删除或不存在</p>
      <NuxtLink to="/archive" class="back-link">返回归档</NuxtLink>
    </div>

    <!-- 内容 -->
    <div v-else-if="series" class="series-content">
      <!-- 系列头部 -->
      <header class="series-header">
        <!-- 封面图 -->
        <div v-if="series.CoverImage" class="series-cover">
          <ClientOnly>
            <BlurhashImage :src="getImageUrl(series.CoverImage)" hash="L6PZfSi_.AyE_3t7t7R**0o#DgR4" :alt="series.Name"
              class="cover-image" />
          </ClientOnly>
        </div>

        <!-- 系列信息 -->
        <div class="series-info">
          <h1 class="series-name">{{ series.Name }}</h1>
          <p v-if="series.Description" class="series-description">{{ series.Description }}</p>
          <div class="series-meta">
            <span class="article-count">共 {{ articles.length }} 篇文章</span>
          </div>
        </div>
      </header>

      <!-- 文章列表 -->
      <section class="articles-section">
        <h2 class="section-title">文章列表</h2>

        <div v-if="articles.length === 0" class="empty-state">
          <p>该系列暂无文章</p>
        </div>

        <ol v-else class="article-list">
          <li v-for="(article, index) in articles" :key="article.ArticleId" class="article-item">
            <NuxtLink :to="`/blog/${article.Slug}`" class="article-link">
              <span class="article-index">{{ index + 1 }}</span>
              <div class="article-content">
                <h3 class="article-title">
                  <span v-if="article.IsPublic === 0" class="lock-icon" title="私密">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16"
                      height="16">
                      <path fill-rule="evenodd"
                        d="M12 1.5a5.25 5.25 0 0 0-5.25 5.25v3a3 3 0 0 0-3 3v6.75a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3v-6.75a3 3 0 0 0-3-3v-3c0-2.9-2.35-5.25-5.25-5.25Zm3.75 8.25v-3a3.75 3.75 0 1 0-7.5 0v3h7.5Z"
                        clip-rule="evenodd" />
                    </svg>
                  </span>
                  {{ article.Title }}
                </h3>
                <p v-if="article.Summary" class="article-summary">{{ article.Summary }}</p>
                <span class="article-date">{{ formatDate(article.CreatedAt) }}</span>
              </div>
            </NuxtLink>
          </li>
        </ol>
      </section>
    </div>
  </div>
</template>

<style scoped>
.series-page {
  max-width: 900px;
  margin: 0 auto;
}

.loading,
.error {
  text-align: center;
  padding: 80px 20px;
  color: #666;
}

.error h2 {
  font-size: 1.5rem;
  margin-bottom: 12px;
  color: var(--text-color);
}

.back-link {
  display: inline-block;
  margin-top: 16px;
  color: var(--accent-color, #5B6FB5);
  text-decoration: none;
}

.back-link:hover {
  text-decoration: underline;
}

/* 系列头部 */
.series-header {
  margin-bottom: 48px;
}

.series-cover {
  width: 100%;
  aspect-ratio: 21/9;
  overflow: hidden;
  margin-bottom: 24px;
  background: #f5f5f5;
}

.cover-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.series-info {
  text-align: center;
}

.series-name {
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 16px;
  color: var(--text-color);
  line-height: 1.3;
}

.series-description {
  font-size: 1.125rem;
  color: #666;
  margin: 0 0 16px;
  line-height: 1.6;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.series-meta {
  font-size: 0.875rem;
  color: #999;
}

/* 文章列表 */
.articles-section {
  border-top: 1px solid #eaeaea;
  padding-top: 32px;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 24px;
  color: var(--text-color);
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #666;
}

.article-list {
  list-style: none;
  padding: 0;
  margin: 0;
  counter-reset: article-counter;
}

.article-item {
  border-bottom: 1px solid #f0f0f0;
}

.article-item:last-child {
  border-bottom: none;
}

.article-link {
  display: flex;
  gap: 16px;
  padding: 20px 0;
  text-decoration: none;
  color: inherit;
  transition: opacity 0.2s;
}

.article-link:hover {
  opacity: 0.8;
}

.article-index {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-color, #f9f9f9);
  color: #666;
  font-size: 0.875rem;
  font-weight: 600;
}

.article-content {
  flex: 1;
  min-width: 0;
}

.article-title {
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 8px;
  color: var(--text-color);
  display: flex;
  align-items: center;
  gap: 6px;
}

.lock-icon {
  color: #999;
  flex-shrink: 0;
}

.article-summary {
  font-size: 0.9375rem;
  color: #666;
  margin: 0 0 8px;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.article-date {
  font-size: 0.8125rem;
  color: #999;
}

/* 深色模式 */
@media (prefers-color-scheme: dark) {
  .series-cover {
    background: #2a2a2a;
  }

  .series-description {
    color: #aaa;
  }

  .article-index {
    background: #2a2a2a;
  }

  .article-summary {
    color: #aaa;
  }
}
</style>
